import { h } from 'vue'
import { toast } from 'vue-sonner'
import { useAuth } from './useAuth'

const SOUND_FILES: Record<string, string> = {
  default: '/audio/cashregister.wav',
  cashregister: '/audio/cashregister.wav',
  cash2: '/audio/cash2.wav',
  cash3: '/audio/cash3.wav',
}

async function waitForServiceWorkerActivation(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return

  const worker = registration.installing || registration.waiting
  if (!worker) return

  await new Promise<void>((resolve) => {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') resolve()
    })
  })
}

export function playNotificationSound(soundName?: string) {
  const file = SOUND_FILES[soundName ?? 'default'] ?? SOUND_FILES.default

  try {
    const audio = new Audio(file)
    audio.play().catch(() => { /* autoplay pode ser bloqueado até haver interação do usuário */ })
  } catch {
    // ambiente sem suporte a áudio
  }
}

export function usePushNotification() {
  const { upsertPushNotificationToken } = useAuth()

  async function syncToken() {
    try {
      const { messagingPromise, getToken } = await import('@/lib/firebase')
      const messaging = await messagingPromise
      if (!messaging) return

      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope'
      })

      await waitForServiceWorkerActivation(registration)

      const token = await getToken(messaging, {
        vapidKey: process.env.VUE_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
      })

      if (!token) return

      const lastSaved = localStorage.getItem('fcmToken_lastSaved')
      if (lastSaved === token) return

      await upsertPushNotificationToken(token)
      localStorage.setItem('fcmToken_lastSaved', token)
    } catch (error) {
      console.error('Erro ao sincronizar token de notificação:', error)
    }
  }

  async function requestPermission() {
    if (typeof Notification === 'undefined') return

    try {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        await syncToken()
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão de notificação:', error)
    }
  }

  async function listenMessages() {
    try {
      const { messagingPromise, onMessage } = await import('@/lib/firebase')
      const messaging = await messagingPromise
      if (!messaging) return

      onMessage(messaging, (payload) => {
        playNotificationSound(payload.data?.sound)

        const now = new Date()
        const timeString = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

        toast(
          h('div', { class: 'flex justify-between items-start w-full pr-2 font-inter' }, [
            h('span', { class: 'font-bold' }, payload.data?.title || 'Nova notificação'),
            h(
              'span',
              { class: 'text-[10px] opacity-50 font-normal ml-auto transition-opacity absolute top-[16px] right-[14px]' },
              'Hoje ' + timeString
            )
          ]),
          {
            description: payload.data?.body,
            duration: 6000,
            style: {
              backgroundColor: 'rgba(30, 30, 30, 0.4)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              padding: '16px',
              fontFamily: "'Inter', sans-serif"
            },
            classes: {
              toast: 'font-inter',
              title: 'font-inter',
              description: 'font-inter',
              icon: 'flex items-center justify-center !w-8 !h-8'
            },
            icon: h('img', { src: '/favicon.ico', class: 'w-8 h-8 object-contain mr-2' })
          }
        )
      })
    } catch (error) {
      console.error('Erro ao ouvir notificações em primeiro plano:', error)
    }
  }

  return {
    requestPermission,
    syncToken,
    listenMessages,
    playNotificationSound,
  }
}
